'use client'

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Currency, ISection, ITheme, Language, ISocial } from '@/lib/interfaces/interfaces'
import { NextFont } from 'next/dist/compiled/@next/font'
import '../../sass/menu/ModernMenu.scss'
import ModernNavigationCarousel from './ModernNavigationCarousel'
import ModernSection from './ModernSection'
import Header from './Header'
import Footer from './Footer'
import CartaSelector, { CartaInfo } from './CartaSelector'
import EmptyMenuSection from './EmptyMenuSection'
import { isPartnerDemoBar, partnerBadgeText } from '@/lib/utils/partner'

interface StructuredAddress {
  streetAddress?: string
  locality?: string
  region?: string
  postalCode?: string
  country?: string
}

interface Props {
  menu: ISection[]
  theme: ITheme
  fontStyle: {
    fontFamily: NextFont
    fontSize: string
  }
  language: Language
  slug: string
  showAds: boolean | undefined
  currency: Currency
  orderingActive: boolean
  priceMarkup?: number
  // Props adicionales para Header y Footer
  socials: ISocial[]
  name: string
  logo: string | undefined
  headerBgImg: string | undefined
  menuTheme: string
  menuSub: boolean | undefined
  activeTranslations: boolean
  defaultLanguage: Language
  activeLanguages: Language[]
  address: string
  structuredAddress?: StructuredAddress
  phone: string
  bookingsActive: boolean
  showMenuTitle?: boolean
  showMenuLogo?: boolean
  cartas?: CartaInfo[]
  activeCartaId?: string
  isEmptyMenu?: boolean
}

const ModernMenuPage = ({
  menu,
  theme,
  fontStyle,
  language,
  slug,
  showAds,
  currency,
  orderingActive,
  priceMarkup = 0,
  socials,
  name,
  logo,
  headerBgImg,
  menuTheme,
  menuSub,
  activeTranslations,
  defaultLanguage,
  activeLanguages,
  address,
  structuredAddress,
  phone,
  bookingsActive,
  showMenuTitle,
  showMenuLogo,
  cartas,
  activeCartaId,
  isEmptyMenu = false
}: Props) => {
  // Filtrar secciones que tienen items visibles y no están ocultas (visible !== false)
  const visibleSections = menu.filter(section => {
    // Primero verificar si la sección está marcada como oculta
    if (section.visible === false) return false

    if (section.type === 'infoBlock') return true
    if (section.type === 'imageSection') return !!section.imageblock?.url
    if (section.type === 'googleReview') return !!section.googleReview?.url

    // Filtrar items visibles y disponibles para delivery si está en modo ordering
    const visibleItems = section.menuItems.filter(item => {
      if (!item.visible) return false
      if (orderingActive && item.availableForDelivery === false) return false
      return true
    })

    return visibleItems.length > 0
  })

  // Inicializar con la primera sección visible
  const [activeSection, setActiveSection] = useState<string>(visibleSections[0]?.sectionId || '')

  // Al cambiar de carta (nueva prop), mostrar la primera sección de la nueva carta
  useEffect(() => {
    setActiveSection(visibleSections[0]?.sectionId || '')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCartaId])

  const touchStartX = useRef<number>(0)
  const touchEndX = useRef<number>(0)
  const touchStartY = useRef<number>(0)
  const touchEndY = useRef<number>(0)

  const handleSectionChange = useCallback((sectionId: string) => {
    setActiveSection(sectionId)

    // Hacer scroll al contenido del menú moderno con offset de 80px
    const menuContentElement = document.querySelector('.modern-menu-content') as HTMLElement
    if (menuContentElement) {
      const elementPosition = menuContentElement.offsetTop
      const offsetPosition = elementPosition - 75

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
    }
  }, [])

  // Funciones para navegar entre secciones
  const goToNextSection = useCallback(() => {
    const currentIndex = visibleSections.findIndex(section => section.sectionId === activeSection)
    if (currentIndex < visibleSections.length - 1) {
      const nextSection = visibleSections[currentIndex + 1]
      handleSectionChange(nextSection.sectionId)
    }
  }, [activeSection, visibleSections, handleSectionChange])

  const goToPreviousSection = useCallback(() => {
    const currentIndex = visibleSections.findIndex(section => section.sectionId === activeSection)
    if (currentIndex > 0) {
      const previousSection = visibleSections[currentIndex - 1]
      handleSectionChange(previousSection.sectionId)
    }
  }, [activeSection, visibleSections, handleSectionChange])

  const handleSwipeGesture = useCallback(() => {
    const swipeThreshold = 70 // Mínima distancia horizontal para considerar un swipe
    const horizontalDistance = touchStartX.current - touchEndX.current
    const verticalDistance = touchStartY.current - touchEndY.current

    // Ignorar gestos donde predomina el desplazamiento vertical (scroll)
    if (Math.abs(horizontalDistance) <= Math.abs(verticalDistance)) {
      return
    }

    if (Math.abs(horizontalDistance) > swipeThreshold) {
      if (horizontalDistance > 0) {
        // Swipe hacia la izquierda - ir a la siguiente sección
        goToNextSection()
      } else {
        // Swipe hacia la derecha - ir a la sección anterior
        goToPreviousSection()
      }
    }
  }, [goToNextSection, goToPreviousSection])

  // Manejo de gestos táctiles
  const handleTouchStart = useCallback((e: TouchEvent) => {
    touchStartX.current = e.changedTouches[0].screenX
    touchStartY.current = e.changedTouches[0].screenY
  }, [])

  const handleTouchEnd = useCallback((e: TouchEvent) => {
    touchEndX.current = e.changedTouches[0].screenX
    touchEndY.current = e.changedTouches[0].screenY
    handleSwipeGesture()
  }, [handleSwipeGesture])

  // Manejo del scroll horizontal con rueda del mouse
  const handleWheel = useCallback((e: WheelEvent) => {
    // Solo procesar scroll horizontal o cuando se mantiene Shift
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY) || e.shiftKey) {
      e.preventDefault()

      const scrollThreshold = 50
      if (Math.abs(e.deltaX) > scrollThreshold) {
        if (e.deltaX > 0) {
          goToNextSection()
        } else {
          goToPreviousSection()
        }
      }
    }
  }, [goToNextSection, goToPreviousSection])

  // Agregar event listeners
  useEffect(() => {
    const menuContentElement = document.querySelector('.modern-menu-content') as HTMLElement

    if (menuContentElement) {
      // Eventos táctiles
      menuContentElement.addEventListener('touchstart', handleTouchStart, { passive: true })
      menuContentElement.addEventListener('touchend', handleTouchEnd, { passive: true })

      // Evento de rueda del mouse
      menuContentElement.addEventListener('wheel', handleWheel, { passive: false })
    }

    return () => {
      if (menuContentElement) {
        menuContentElement.removeEventListener('touchstart', handleTouchStart)
        menuContentElement.removeEventListener('touchend', handleTouchEnd)
        menuContentElement.removeEventListener('wheel', handleWheel)
      }
    }
  }, [handleTouchStart, handleTouchEnd, handleWheel])

  const activeSectionData = visibleSections.find(section => section.sectionId === activeSection)
  const isPartnerDemo = isPartnerDemoBar({ slug })
  const activeSectionIndex = visibleSections.findIndex(section => section.sectionId === activeSection)
  const isActivePartner = isPartnerDemo && activeSectionIndex === 0
  const partnerBadge = isActivePartner
    ? (partnerBadgeText[language] || 'Recommended')
    : undefined

  return (
    <div className={`modern-menu${cartas && cartas.length > 1 ? ' has-carta-selector' : ''}`} style={{ background: theme.footer.backgroundColor }}>
      <Header
        socials={socials}
        name={name}
        logo={logo}
        headerBgImg={headerBgImg}
        menuTheme={menuTheme}
        menuSub={menuSub || false}
        slug={slug}
        language={language}
        activeTranslations={activeTranslations}
        defaultLanguage={defaultLanguage}
        activeLanguages={activeLanguages}
        showMenuTitle={showMenuTitle}
        showMenuLogo={showMenuLogo}
      />

      {cartas && cartas.length > 1 && activeCartaId && (
        <CartaSelector
          cartas={cartas}
          activeCartaId={activeCartaId}
          variant='modern'
          preserveDelivery={orderingActive}
          theme={theme}
        />
      )}

      {!isEmptyMenu && (
        <ModernNavigationCarousel
          sections={visibleSections}
          activeSection={activeSection}
          onSectionChange={handleSectionChange}
          language={language}
          orderingActive={orderingActive}
          theme={theme}
        />
      )}

      <div className='modern-menu-content' style={{ background: theme.background }}>
        {isEmptyMenu
          ? (
            <EmptyMenuSection
              theme={theme}
              fontStyle={fontStyle}
              language={language}
            />
            )
          : activeSectionData && (
            <ModernSection
            section={activeSectionData}
            theme={theme}
            fontStyle={fontStyle}
            language={language}
            defaultLanguage={defaultLanguage}
            currency={currency}
            orderingActive={orderingActive}
            priceMarkup={priceMarkup}
            showAds={showAds}
            isPartner={isActivePartner}
            partnerBadge={partnerBadge}
          />
        )}
      </div>

      <Footer
        slug={slug}
        bookingsActive={bookingsActive}
        name={name}
        address={address}
        structuredAddress={structuredAddress}
        phone={phone}
        theme={theme}
        language={language}
      />
    </div>
  )
}

export default ModernMenuPage
